import * as p from '@clack/prompts'
import type { VercelNeonFlowResult } from '@cli-types/next-adapter/vercel.js'
import { redactSecrets } from '@cli-utils/process.js'
import { spinner } from '@cli-utils/terminal.js'
import pc from 'picocolors'
import { ensureVercelAuth } from '../auth/ensure-vercel-auth.js'
import { guardEnvLocal } from '../env-guard.js'
import { resolveVercelRunner } from '../runner/resolve-vercel-runner.js'
import { authFailureMessage } from './auth-failure-message.js'
import { ensureLinkedProject } from './ensure-linked-project.js'
import { neonFailureMessage } from './neon-failure-message.js'
import { provisionNeonForMode } from './provision-neon-for-mode.js'
import { pullNeonDatabaseUrl } from './pull-neon-database-url.js'

export async function runVercelNeonFlow(options: {
  cwd: string
  projectName: string
  interactive: boolean
  plan?: string
  env?: NodeJS.ProcessEnv
}): Promise<VercelNeonFlowResult> {
  const env = options.env ?? process.env
  const envGuard = guardEnvLocal(options.cwd)

  try {
    const runnerSpinner = spinner()
    runnerSpinner.start('Checking for the Vercel CLI')
    const runner = await resolveVercelRunner()
    runnerSpinner.clear()

    const auth = await ensureVercelAuth(runner, options.cwd, {
      env,
      allowLogin: options.interactive
    })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      return { ok: false }
    }

    await ensureLinkedProject(runner, options.cwd, options.projectName, env, auth.username)

    const neon = await provisionNeonForMode(runner, options)
    if (neon.failure) {
      p.log.warn(neonFailureMessage(neon.failure))
      if (neon.detail) p.log.message(pc.dim(redactSecrets(neon.detail)))
      return { ok: false }
    }

    const databaseUrl = await pullNeonDatabaseUrl(runner, options.cwd, env)

    // Erasing the note is only safe when every row printed below it is known: the terminal-attached
    // fallback and the no-DATABASE_URL warning both add unknown rows.
    const dismissSignedInNote =
      databaseUrl && auth.dismissNote && !neon.usedTerminalFallback
        ? () => auth.dismissNote?.({ linesBelow: 0 })
        : undefined

    return {
      ok: true,
      databaseUrl,
      dashboardUrl: neon.dashboardUrl,
      resourceUrl: neon.resourceUrl,
      dismissSignedInNote
    }
  } catch (error) {
    p.log.warn(
      `Vercel provisioning failed: ${error instanceof Error ? error.message : String(error)}`
    )
    return { ok: false }
  } finally {
    envGuard.restore()
  }
}
